import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { Button } from 'antd';
import Image from 'next/image';
import { HeroData, InsAccountModel } from '@/types/home';
import { cn } from '@/lib/utils';

interface TopSearchContentProps {
  inputClassName?: string;
  inputIcon?: React.ReactNode;
  inputPlaceholder?: string;
  showSearchBtnWithFullWidth?: boolean;
  btnPlaceholder?: string;
  topInfo?: HeroData;
  middleContent?: React.ReactNode;
  loading?: boolean;
  searchVal?: string;
  onSearchClick?: (searchStr?: string) => void;
  onValueChanged?: (e?: string) => void;
  setType?: (type: 'following' | 'followers') => void;
  type?: 'following' | 'followers';
  currentInsUser?: InsAccountModel;
}

interface RadioTabsProps {
  value: 'followers' | 'following';
  onChange: (val: 'followers' | 'following') => void;
  t: (str: string) => string;
  currentInsUser?: InsAccountModel;
}

const RadioTabs: React.FC<RadioTabsProps> = ({ value, onChange, t, currentInsUser }) => (
  <div className="flex gap-8 items-center lg:my-6 my-4">
    <label className="flex items-center cursor-pointer select-none gap-2">
      <input
        type="radio"
        name="tab"
        value="followers"
        checked={value === 'followers'}
        onChange={() => onChange('followers')}
        className="peer hidden"
      />
      <span
        className={`
        w-4 h-4 rounded-full border-2 flex items-center justify-center
        ${value === 'followers' ? 'border-[#D5CEFF]' : 'border-gray-300'}
      `}
      >
        {value === 'followers' && <span className="w-2.5 h-2.5 bg-[#7b61ff] rounded-full"></span>}
      </span>
      <span className={value === 'followers' ? 'text-[#7b61ff] font-medium' : 'text-gray-800 font-medium'}>
        {t('Followers')}
      </span>
    </label>
    <label className="flex items-center cursor-pointer select-none gap-2">
      <input
        type="radio"
        name="tab"
        value="following"
        checked={value === 'following'}
        onChange={() => onChange('following')}
        className="peer hidden"
      />
      <span
        className={`
        w-4 h-4 rounded-full border-2 flex items-center justify-center
        ${value === 'following' ? 'border-[#D5CEFF]' : 'border-gray-300'}
      `}
      >
        {value === 'following' && <span className="w-2.5 h-2.5 bg-[#7b61ff] rounded-full"></span>}
      </span>
      <span className={value === 'following' ? 'text-[#7b61ff] font-medium' : 'text-gray-800 font-medium'}>
        {t('Following')}
      </span>
    </label>
  </div>
);

const TopSearchContent = ({
  inputClassName,
  inputIcon,
  inputPlaceholder,
  showSearchBtnWithFullWidth,
  btnPlaceholder,
  topInfo,
  middleContent,
  loading,
  searchVal,
  onSearchClick,
  onValueChanged,
  setType,
  type,
  currentInsUser,
}: TopSearchContentProps) => {
  const { t } = useTranslation('common');
  const [searchStr, setSearchStr] = useState(searchVal);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchVal !== searchStr) {
      setSearchStr(searchVal);
    }
  }, [searchVal]);

  const renderBasicContent = () => {
    const Title = topInfo?.seoTitle ? 'p' : 'h1';

    return (
      <>
        {topInfo?.title && (
          <Title className={styles.title} style={topInfo.titleStyle}>
            {topInfo?.title}
          </Title>
        )}

        {topInfo?.seoTitle && (
          <h1 className={styles.title} style={{ display: 'none' }}>
            {topInfo?.seoTitle}
          </h1>
        )}

        {topInfo?.subTitle && (
          <p className={styles.subTitle} style={topInfo.subTitleStyle}>
            {topInfo?.subTitle}
          </p>
        )}

        {topInfo?.desc && (
          <p className={styles.subDesc} style={topInfo.descStyle}>
            {topInfo?.desc}
          </p>
        )}
      </>
    );
  };

  const renderSearch = () => {
    return (
      <div
        className={cn({
          'p-4 rounded-b-xl': true,
        })}
      >
        <div className={cn(styles.inputContainer, 'w-[590px] pl-4 py-2 pr-2 bg-white', inputClassName)} ref={inputRef}>
          {inputIcon ? (
            inputIcon
          ) : (
            <Image src={'/images/home/instagram-icon.webp'} alt="instagram" width={24} height={24} />
          )}
          <input
            value={searchStr}
            onChange={(e) => {
              setSearchStr(e.target.value);
              onValueChanged && onValueChanged(e?.target?.value);
            }}
            onKeyDown={(e) => {
              if (e?.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value;
                onSearchClick(value);
              }
            }}
            placeholder={inputPlaceholder ?? t('insert link or username')}
          />
          <Button
            loading={loading}
            className={cn(styles.searchBtn, 'w-[220px] max-md:min-w-[100px]', {
              'max-md:hidden': showSearchBtnWithFullWidth,
            })}
            onClick={() => {
              onSearchClick(searchStr);
            }}
          >
            {btnPlaceholder ?? t('Search Now')}
          </Button>
        </div>
        <Button
          loading={loading}
          className={cn(styles.searchBtnWithFullWidth, {
            hidden: true,
            'max-md:block': showSearchBtnWithFullWidth,
          })}
          onClick={() => {
            onSearchClick(searchStr);
          }}
        >
          {btnPlaceholder ?? t('Search Now')}
        </Button>
      </div>
    );
  };

  if (!topInfo) {
    return null;
  }

  return (
    <div className={styles.content}>
      {renderBasicContent()}
      {!currentInsUser && (
        <>
          <div className="flex justify-center">
            <RadioTabs value={type} onChange={setType} t={t} />
          </div>
          {renderSearch()}
          {middleContent}
        </>
      )}
    </div>
  );
};

export default TopSearchContent;
